import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Outlet, useLocation, useSearchParams } from 'react-router';

import { selectIsGuest, selectRole, selectSessionStatus, selectUser } from '@/shared/api/sessionSlice';
import type { Permission } from '@/shared/api/types';
import { holds, homeUrl, type RoleName } from '@/shared/lib/roles';
import { Spinner } from '@/shared/ui/primitives';

import { afterSignIn, GoTo, signInUrl } from './destination';

/**
 * Routes below need a signed-in account; others are sent to sign in and brought back
 * after. A guest is sent too: signing in is what keeps what it did.
 */
export function RequireAuth() {
  const { t } = useTranslation();
  const status = useSelector(selectSessionStatus);
  const guest = useSelector(selectIsGuest);
  const user = useSelector(selectUser);
  const location = useLocation();

  if (status === 'unknown') return <Spinner label={t('loading')} />;
  if (status === 'anonymous' || guest) {
    return <GoTo to={signInUrl(location)} state={{ from: location.pathname }} />;
  }
  // An account that manages others and has no second factor yet may do nothing else
  // before setting one up (the server refuses the rest): its settings say how.
  if (user?.secondFactorRequired && location.pathname !== '/settings') {
    return <GoTo to="/settings" />;
  }
  return <Outlet />;
}

/**
 * Routes below belong to the interface of these roles. Others are sent to the home
 * of their own, on whichever host that is: the pages are not something they are
 * refused, they simply are not part of their application. This only keeps the
 * pages out of sight; the API checks every request.
 *
 * With `visitors`, they are also for anybody without an account (a visitor, a
 * guest): what is public. An account of another role still goes to its own home.
 */
export function RequireRole({
  roles,
  visitors = false,
  permission,
}: {
  roles: readonly RoleName[];
  visitors?: boolean;
  /** And, for the accounts of these roles, this permission (root holds them all). */
  permission?: Permission;
}) {
  const { t } = useTranslation();
  const status = useSelector(selectSessionStatus);
  const role = useSelector(selectRole);
  const user = useSelector(selectUser);
  if (visitors && status === 'unknown') return <Spinner label={t('loading')} />;
  const held = permission === undefined || holds(role, user?.permissions, permission);
  const allowed = role === undefined ? visitors : roles.includes(role) && held;
  return allowed ? <Outlet /> : <GoTo to={homeUrl(role)} />;
}

/** A page that is nobody's own: whoever opens it is sent to where their role starts. */
export function ToHome() {
  return <GoTo to={homeUrl(useSelector(selectRole))} />;
}

/** The sign-in pages make no sense once signed in. */
export function RequireAnonymous() {
  const { t } = useTranslation();
  const status = useSelector(selectSessionStatus);
  const role = useSelector(selectRole);
  const location = useLocation();
  const [params] = useSearchParams();

  if (status === 'unknown') return <Spinner label={t('loading')} />;
  if (status === 'authenticated' && role) {
    // Back to the page that asked for the sign-in, or to where this role starts.
    const asked = params.get('next') ?? (location.state as { from?: string } | null)?.from;
    return <GoTo to={afterSignIn(role, asked)} />;
  }
  return <Outlet />;
}
